import { useId, useRef, useState, type ReactElement } from 'react'

import { AnimatePresence, motion, Reorder, useDragControls } from 'motion/react'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'
import { cn } from '../../../utils/cn'
import { Button, type ButtonProps } from '../../atoms/button/button'
import { Text } from '../../atoms/text/text'
import { Switch } from '../../molecules/switch/switch'

import { OmniTableIcon } from './omni-table-icon'
import { OmniTablePopover } from './omni-table-popover'

export interface OmniTableColumnOption {
  id: string
  label: string
  visible: boolean
}

/** The one column a change moved or showed or hid. */
export interface OmniTableColumnChange {
  kind: 'move' | 'visibility'
  columnId: string
  /**
   * The column is still being dragged: the order is a live preview for the
   * table to follow, not one to store. The drop reports once more without it.
   */
  dragging?: boolean
}

export interface OmniTableColumnEditorProps {
  trigger: ReactElement<ButtonProps>
  /** The consumer owns column order and visibility, scoped to its table or tab. */
  columns: readonly OmniTableColumnOption[]
  /**
   * Reports every reorder while a column is dragged (`change.dragging`) and
   * once more on drop; visibility and keyboard moves report once.
   */
  onChange: (
    columns: OmniTableColumnOption[],
    change: OmniTableColumnChange
  ) => void
  onReset: () => void
  isCustomized: boolean
  animated?: boolean
  /** Called when the panel opens or closes. */
  onOpenChange?: (open: boolean) => void
}

function ColumnEditorRow({
  column,
  instructionsId,
  animated,
  onToggle,
  onMove,
  onDragStart,
  onDragEnd
}: {
  column: OmniTableColumnOption
  instructionsId: string
  animated: boolean
  onToggle: () => void
  onMove: (direction: 'up' | 'down' | 'first' | 'last') => void
  onDragStart: () => void
  onDragEnd: () => void
}) {
  const controls = useDragControls()
  const labelId = useId()
  const [dragging, setDragging] = useState(false)
  return (
    <Reorder.Item
      as='div'
      role='listitem'
      value={column.id}
      dragListener={false}
      dragControls={controls}
      layout='position'
      transition={{
        layout: animated
          ? { type: 'spring', stiffness: 500, damping: 35, mass: 0.7 }
          : { duration: 0 },
        scale: { duration: animated ? 0.15 : 0 }
      }}
      whileDrag={{ scale: animated ? 1.02 : 1 }}
      onDragStart={() => {
        setDragging(true)
        onDragStart()
      }}
      onDragEnd={() => {
        setDragging(false)
        onDragEnd()
      }}
      variants={
        animated
          ? { closed: { opacity: 0, x: -6 }, open: { opacity: 1, x: 0 } }
          : undefined
      }
      className={cn(
        'border-surface-200 bg-surface text-body-lg relative flex h-[51px] items-center gap-2 border-b px-3 last:border-b-0',
        dragging && 'shadow-dialog'
      )}
    >
      <button
        type='button'
        aria-label={`Reorder ${column.label}`}
        aria-describedby={instructionsId}
        className='text-surface-body focus-visible:ring-primary-200 flex shrink-0 cursor-grab items-center rounded outline-none focus-visible:ring-2 active:cursor-grabbing'
        style={{ touchAction: 'none' }}
        onPointerDown={event => controls.start(event)}
        onKeyDown={event => {
          const direction = {
            ArrowUp: 'up',
            ArrowDown: 'down',
            Home: 'first',
            End: 'last'
          } as const
          const movement = direction[event.key as keyof typeof direction]
          if (movement) {
            event.preventDefault()
            onMove(movement)
          }
        }}
      >
        <OmniTableIcon name='drag_indicator' size='inherit' />
      </button>
      <Text
        as='span'
        variant='body-lg'
        weight={column.visible ? 'semibold' : 'regular'}
        id={labelId}
        className={cn(
          'min-w-0 flex-1 truncate',
          column.visible ? 'text-surface-title-50' : 'text-surface-body'
        )}
        title={column.label}
      >
        {column.label}
      </Text>
      <Switch
        aria-labelledby={labelId}
        size='lg'
        checked={column.visible}
        onCheckedChange={onToggle}
        // Scale the official lg geometry (42×24 box, 36.6×19.2 track) with the label.
        className='data-checked:[&_[data-slot=switch-track]]:bg-surface-title-50! h-[1lh] w-[1.75lh] [&_[data-slot=switch-track]]:[--track-h:0.8lh] [&_[data-slot=switch-track]]:[--track-w:1.525lh]'
      />
    </Reorder.Item>
  )
}

/** Shared editor; actions and other fixed controls stay outside the editable column list. */
export function OmniTableColumnEditor({
  trigger,
  columns,
  onChange,
  onReset,
  isCustomized,
  animated = true,
  onOpenChange
}: OmniTableColumnEditorProps) {
  const instructionsId = useId()
  const [open, setOpen] = useState(false)
  const reducedMotion = useReducedMotion()
  const shouldAnimate = animated && !reducedMotion
  const byId = new Map(columns.map(column => [column.id, column]))
  const reorder = (
    ids: readonly string[],
    columnId: string,
    dragging?: boolean
  ) =>
    onChange(
      ids.flatMap(id => byId.get(id) ?? []),
      {
        kind: 'move',
        columnId,
        ...(dragging && { dragging })
      }
    )
  // A drag reorders this draft as the column passes others and previews each
  // order to the table; the order is committed once, on drop.
  const [draft, setDraft] = useState<string[] | null>(null)
  const draftRef = useRef<string[] | null>(null)
  const dragged = useRef<string>()
  const previewed = useRef(false)
  const updateDraft = (ids: string[] | null) => {
    draftRef.current = ids
    setDraft(ids)
  }
  const previewOrder = (order: string[]) => {
    updateDraft(order)
    if (!dragged.current) return
    previewed.current = true
    reorder(order, dragged.current, true)
  }
  const ids = columns.map(column => column.id)
  const shown = draft ? draft.flatMap(id => byId.get(id) ?? []) : columns
  const move = (id: string, direction: 'up' | 'down' | 'first' | 'last') => {
    const next = [...ids]
    const index = next.indexOf(id)
    const nextIndex =
      direction === 'first'
        ? 0
        : direction === 'last'
        ? ids.length - 1
        : index + (direction === 'up' ? -1 : 1)
    if (nextIndex < 0 || nextIndex >= next.length || nextIndex === index) return
    next.splice(index, 1)
    next.splice(nextIndex, 0, id)
    reorder(next, id)
  }
  return (
    <OmniTablePopover
      trigger={trigger}
      open={open}
      onOpenChange={next => {
        setOpen(next)
        onOpenChange?.(next)
      }}
      label='Edit columns'
      align='end'
      animated={animated}
      className='font-body border-surface-200 bg-surface shadow-dialog w-max min-w-64 max-w-[calc(100vw-32px)] rounded-xl border p-2'
    >
      <span id={instructionsId} className='sr-only'>
        Drag to reorder, or use Arrow Up, Arrow Down, Home and End.
      </span>
      <Reorder.Group
        as='div'
        axis='y'
        role='list'
        aria-label='Table columns'
        values={draft ?? ids}
        onReorder={previewOrder}
      >
        {shown.map(column => (
          <ColumnEditorRow
            key={column.id}
            column={column}
            instructionsId={instructionsId}
            animated={shouldAnimate}
            onToggle={() =>
              onChange(
                columns.map(item =>
                  item.id === column.id
                    ? { ...item, visible: !item.visible }
                    : item
                ),
                { kind: 'visibility', columnId: column.id }
              )
            }
            onMove={direction => move(column.id, direction)}
            onDragStart={() => {
              dragged.current = column.id
              previewed.current = false
            }}
            onDragEnd={() => {
              const order = draftRef.current ?? ids
              const moved = order.some((id, index) => id !== ids[index])
              updateDraft(null)
              dragged.current = undefined
              // Settle the previewed order, even back where it started.
              if (moved || previewed.current) reorder(order, column.id)
              previewed.current = false
            }}
          />
        ))}
      </Reorder.Group>
      <AnimatePresence initial={false}>
        {isCustomized && (
          <motion.div
            key='reset'
            className='mb-3 ml-3 mt-4'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: shouldAnimate ? 0.15 : 0 }}
          >
            <Button
              variant='tertiary'
              size='sm'
              className='text-link-fresh text-body-lg h-auto min-w-0 justify-start rounded-none border-0 p-0 underline !decoration-from-font [text-underline-position:from-font]'
              onClick={onReset}
            >
              Reset to default view
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </OmniTablePopover>
  )
}
